import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Select from "react-select";
import { clienteService } from "../services/clienteService";
import { planilhaService } from "../services/planilhaService";
import { Button, Input, LoadingSpinner } from "../components/common";
import { useFeedback } from "../contexts/FeedbackContext";
import { HiXCircle, HiSave, HiX } from "react-icons/hi";
import { PlanilhaStatus } from "@models/index";
import { WRIKE_PIT_ERRO_FORMATO, linkPitValido } from "@utils/wrikeLink";

interface Cliente {
  id: string;
  nome: string;
  email: string;
}

const UF_ESTADOS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

interface FormData {
  clienteId: string;
  evento: string;
  dataInicio: string;
  dataFim?: string;
  localEvento?: string;
  ufEvento?: string;
  numeroOC?: string;
  linkProjeto?: string;
}

function NovaPlanilha() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useFeedback();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    clienteId: "",
    evento: "",
    dataInicio: "",
    dataFim: "",
    localEvento: "",
    ufEvento: "",
    numeroOC: "",
    linkProjeto: "",
  });

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const data = await clienteService.getAll({ pageSize: 1000 }); // Pega todos os clientes
      setClientes(data.items);
      setError(null);
    } catch (err) {
      setError("Erro ao carregar clientes");
      console.error(err);
      showError("Erro ao carregar clientes. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Permitir apenas números no campo OC
    if (name === "numeroOC") {
      const onlyNumbers = value.replace(/[^0-9]/g, "");
      setFormData({ ...formData, [name]: onlyNumbers });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clienteId || !formData.evento || !formData.dataInicio) {
      setError("Por favor, preencha todos os campos obrigatórios");
      showError("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    if (formData.linkProjeto && !linkPitValido(formData.linkProjeto)) {
      setError(
        "Link PIT inválido. Use o formato: https://www.wrike.com/open.htm?id=XXXXXXXXX",
      );
      showError("Link PIT com formato inválido.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Encontrar nome do cliente
      const cliente = clientes.find((c) => c.id === formData.clienteId);

      const novaPlanilha = {
        ...formData,
        clienteNome: cliente ? cliente.nome : "",
        status: PlanilhaStatus.EM_ANDAMENTO,
        totalGeral: 0,
        itens: [],
      };

      const planilhaCriada = await planilhaService.create(novaPlanilha);
      showSuccess("Planilha criada com sucesso!");
      navigate(`/planilhas/editar/${planilhaCriada.id}`);
    } catch (err) {
      setError("Erro ao criar planilha");
      console.error(err);
      showError(
        "Não foi possível criar a planilha. Verifique os dados e tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Carregando clientes..." />;
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-base-content mb-2">
          Nova Planilha
        </h2>
        <p className="text-base-content/70">
          Crie uma nova planilha de orçamento
        </p>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <HiXCircle className="shrink-0 h-6 w-6" />
          <span>{error}</span>
        </div>
      )}

      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h3 className="text-2xl font-bold mb-4">Informações Básicas</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium text-base-content">
                  Cliente *
                </span>
              </label>
              <Select
                name="clienteId"
                value={
                  formData.clienteId
                    ? clientes
                        .filter((c) => c.id === formData.clienteId)
                        .map((c) => ({ value: c.id, label: c.nome }))[0]
                    : null
                }
                onChange={(option) =>
                  handleChange({
                    target: {
                      name: "clienteId",
                      value: option?.value || "",
                    },
                  } as any)
                }
                options={clientes.map((cliente) => ({
                  value: cliente.id,
                  label: cliente.nome,
                }))}
                placeholder="Selecione um cliente"
                isClearable
                isSearchable
                required
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "48px",
                    borderColor: "#e5e7eb",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 50,
                  }),
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#3b82f6",
                    primary25: "#eff6ff",
                  },
                })}
              />
              {clientes && clientes.length === 0 && (
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Nenhum cliente cadastrado.{" "}
                    <Link to="/clientes" className="link link-primary">
                      Cadastre um cliente primeiro
                    </Link>
                  </span>
                </label>
              )}
            </div>

            <Input
              label="Evento/Ação *"
              type="text"
              name="evento"
              value={formData.evento}
              onChange={handleChange}
              required
              placeholder="Ex: ASSINATURA PROJETOS SOCIAIS REFINARIA ABREU E LIMA"
              fullWidth
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Data de Início *"
                type="date"
                name="dataInicio"
                value={formData.dataInicio}
                onChange={handleChange}
                required
                fullWidth
              />

              <Input
                label="Data de Fim"
                type="date"
                name="dataFim"
                value={formData.dataFim}
                onChange={handleChange}
                fullWidth
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Cidade do Evento"
                type="text"
                name="localEvento"
                value={formData.localEvento}
                onChange={handleChange}
                placeholder="Ex: São Paulo"
                fullWidth
              />

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium text-base-content">
                    UF do Evento
                  </span>
                </label>
                <select
                  name="ufEvento"
                  value={formData.ufEvento}
                  onChange={handleChange}
                  className="select select-bordered w-full"
                >
                  <option value="">Selecione a UF</option>
                  {UF_ESTADOS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Input
              label="Link PIT"
              type="url"
              name="linkProjeto"
              value={formData.linkProjeto}
              onChange={handleChange}
              placeholder="Ex: https://www.wrike.com/open.htm?id=4431130756"
              fullWidth
            />

            {formData.linkProjeto && !linkPitValido(formData.linkProjeto) && (
              <p className="text-sm text-red-600 -mt-2">
                {WRIKE_PIT_ERRO_FORMATO}
              </p>
            )}

            {/* <Input
              label="Número da OC"
              type="text"
              name="numeroOC"
              value={formData.numeroOC}
              onChange={handleChange}
              placeholder="Ex: 123456"
              fullWidth
            />

            <Input
              label="Link da Pasta da OC no Wrike"
              type="url"
              name="linkWrike"
              value={formData.linkWrike}
              onChange={handleChange}
              placeholder="Ex: https://www.wrike.com/open.htm?id=..."
              fullWidth
            /> */}

            <div className="flex gap-4 mt-6 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate("/planilhas")}
                disabled={submitting}
              >
                <HiX className="h-4 w-4" />
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="success"
                loading={submitting}
                disabled={submitting}
              >
                <HiSave className="h-4 w-4" />
                Criar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NovaPlanilha;
